import { useCallback, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { getErrorMessage } from '../../infrastructure/http/common';
import { useCases } from '../../infrastructure/di/container';
import { ChannelEntity } from '../../domain/entities/Channel';
import { ChannelFilters, DEFAULT_CHANNEL_FILTERS } from '../../shared/types/ChannelFilters';
import { Pagination } from '../types/Pagination';

const DEFAULT_PAGE_SIZE = 10;

export const useChannelPickerSource = (pageSize: number = DEFAULT_PAGE_SIZE) => {
  const [channels, setChannels] = useState<ChannelEntity[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, perPage: pageSize, total: 0 });
  const [filters, setFilters] = useState<ChannelFilters>(DEFAULT_CHANNEL_FILTERS);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const latestRequest = useRef(0);

  const load = useCallback(async (page: number, perPage: number, nextFilters: ChannelFilters, append = false) => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(undefined);
    setFilters(nextFilters);
    try {
      const res = await useCases.listChannels().execute({ page, perPage, ...nextFilters });
      if (request !== latestRequest.current) return;
      setChannels((current) => (append ? [...current, ...res.items] : res.items));
      setPagination({ page: res.page, perPage: res.perPage, total: res.total });
    } catch (e) {
      if (request === latestRequest.current) setError(isAxiosError(e) ? getErrorMessage(e) : 'unknown-error');
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);

  const reset = useCallback(() => load(1, pageSize, DEFAULT_CHANNEL_FILTERS), [load, pageSize]);

  const countChannels = useCallback((nextFilters: ChannelFilters) => useCases.countChannels().execute(nextFilters), []);

  return { channels, pagination, filters, isLoading, error, load, reset, countChannels };
};
